import * as React from 'react';

import { Col, Form, Radio, Row, Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';

import { InterviewComponent } from 'src/components/interviewComponents/types/interviewComponent';
import { nameof } from 'ts-simple-nameof';
import { setJobDetails } from 'src/store/modules/interviewModule';
import { useTranslation } from 'react-i18next';

interface JobDetailsProps {

}

interface JobDetailsFormValues {
    isInputValued: boolean | 'na';
    hasGrowthOpportunity: boolean | 'na';
    hasAppropriateResponsibilities: boolean;
}

export const JobDetails: InterviewComponent<JobDetailsProps> = React.forwardRef((props, ref) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [form] = Form.useForm<JobDetailsFormValues>();

    const jobDetails = useAppSelector((state) => state.interview.jobDetails);

    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                await form.validateFields();

                form.submit();

                return true;
            } catch (err) {
                return false;
            }
        },
    }));

    return (
        <Row
            align="middle"
            justify="center"
            gutter={[30, 20]}
        >
            <Col
                span={24}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        // We're storing 'agree'/'disagree'/'not applicable' values as:
                        // agree = true
                        // disagree = false
                        // not applicable = null
                        // but the form values have issues when defaulting to null.

                        // Lets make the 'not applicable' values 'na' and convert back and forth as needed
                        dispatch(setJobDetails({
                            hasAppropriateResponsibilities: values.hasAppropriateResponsibilities,
                            hasGrowthOpportunity: values.hasGrowthOpportunity === 'na'
                                ? null
                                : values.hasGrowthOpportunity,
                            isInputValued: values.isInputValued === 'na'
                                ? null
                                : values.isInputValued,
                        }));
                    }}
                >
                    <Form.Item
                        label={t('Components.InterviewComponents.JobDetails.is_input_valued_label')}
                        name={nameof<JobDetailsFormValues>(x => x.isInputValued)}
                        rules={[{ required: true }]}
                        // We're storing 'agree'/'disagree'/'not applicable' values as:
                        // agree = true
                        // disagree = false
                        // not applicable = null
                        // but the form values have issues when defaulting to null.

                        // Lets make the 'not applicable' values 'na' and convert back and forth as needed
                        initialValue={jobDetails?.isInputValued === null ? 'na' : jobDetails?.isInputValued}
                    >
                        <Radio.Group>
                            <Space
                                direction="vertical"
                            >
                                <Radio
                                    value={true}
                                >
                                    {t('Common.agree')}
                                </Radio>

                                <Radio
                                    value={false}
                                >
                                    {t('Common.disagree')}
                                </Radio>

                                <Radio
                                    value={'na'}
                                >
                                    {t('Common.not_applicable')}
                                </Radio>
                            </Space>

                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label={t('Components.InterviewComponents.JobDetails.has_growth_opportunities_label')}
                        name={nameof<JobDetailsFormValues>(x => x.hasGrowthOpportunity)}
                        rules={[{ required: true }]}
                        // We're storing 'agree'/'disagree'/'not applicable' values as:
                        // agree = true
                        // disagree = false
                        // not applicable = null
                        // but the form values have issues when defaulting to null.

                        // Lets make the 'not applicable' values 'na' and convert back and forth as needed
                        initialValue={jobDetails?.hasGrowthOpportunity === null ? 'na' : jobDetails?.hasGrowthOpportunity}
                    >
                        <Radio.Group>
                            <Space
                                direction="vertical"
                            >
                                <Radio
                                    value={true}
                                >
                                    {t('Common.agree')}
                                </Radio>

                                <Radio
                                    value={false}
                                >
                                    {t('Common.disagree')}
                                </Radio>

                                <Radio
                                    value={'na'}
                                >
                                    {t('Common.not_applicable')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label={t('Components.InterviewComponents.JobDetails.has_appropriate_responsibilities_label')}
                        name={nameof<JobDetailsFormValues>(x => x.hasAppropriateResponsibilities)}
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: t('Common.validation.required_field_generic_message'),
                            },
                        ]}
                        initialValue={jobDetails?.hasAppropriateResponsibilities}
                    >
                        <Radio.Group>
                            <Space
                                direction="vertical"
                            >
                                <Radio
                                    value={true}
                                >
                                    {t('Common.agree')}
                                </Radio>

                                <Radio
                                    value={false}
                                >
                                    {t('Common.disagree')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Col>
        </Row>
    );
});

JobDetails.displayName = 'JobDetails';